import * as Sentry from '@sentry/react'

/**
 * Report that a TRF was built in the browser instead of fetched from provider-api.
 *
 * Server-side generation is the intended path everywhere (GEN-4159 for a session
 * DRAFT, GEN-4161/4162 for a placed order), and GEN-4242 deletes the in-browser
 * `pdf-lib` renderer entirely. Until then this warning is the only way to tell
 * whether the fallback is still load-bearing in production: each occurrence is a
 * document the browser had to draw, and `reason` says why the server could not
 * supply it. A quiet period here is the evidence GEN-4242 needs.
 *
 * Deliberately a warning-level `captureMessage` rather than a log so it can be
 * alerted on and counted per reason.
 */

/** Why the browser had to build the TRF instead of fetching a server-rendered one. */
export type ClientTrfRenderReason =
  /**
   * The order-details response carried no `trf_pdf_url`. Expected when the TRF
   * bucket is unconfigured for the environment, when the order is not in a placed
   * status, or when the artifact could neither be found nor re-rendered server-side.
   */
  | 'server_artifact_missing'
  /** Neither an ordering session id nor a placed order id was available to fetch with. */
  | 'no_session_or_order_id'
  /** The `/form/complete` route, which has no server-side equivalent. */
  | 'completed_form_route'

export interface ClientTrfRenderWarning {
  reason: ClientTrfRenderReason
  /** Which in-browser generator ran: canonical order, or the legacy TRF payload. */
  renderer: 'canonical' | 'legacy_rfd'
  orderId?: string | null
}

export function warnClientSideTrfRender({ reason, renderer, orderId }: ClientTrfRenderWarning): void {
  Sentry.captureMessage('TRF rendered client-side instead of fetched from provider-api', {
    level: 'warning',
    tags: {
      operation: 'trf.client_render',
      reason,
      renderer,
      ...(orderId ? { orderId } : {}),
    },
  })
}
